import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo } from 'react';
import { Button } from '../../../components/Button';
import useOpen from '../../../hooks/useOpen';
import { T } from '../../../translation/src';
import { BuyCreditsModal } from '../BuyCreditsModal';
import { ContactSalesModal } from '../ContactSalesModal';

export interface NoCreditsBannerProps {
  organisationId: string;
  // Credits that still exist but are reserved by uploads in progress.
  balance: number;
  // What an order costs and may be, as the server has it.
  projectUnitPrice: number;
  minPurchaseAmount: number;
  maxPurchaseAmount: number;
  // Whether this user may buy credits (organisation permission); others must ask an admin.
  canBuyCredits: boolean;
  // The subscription is paused (from Odoo): uploads wait, whatever the balance; buying is beside the point.
  paused?: boolean;
}

// Shown on the projects page while uploads are disabled for lack of credits.
// Says why the upload card is gone and what this user can do about it. Kept
// low-key (outline, no fill) — it sits above the whole project list.
const NoCreditsBanner_: React.FC2<NoCreditsBannerProps> = ({ organisationId, balance, projectUnitPrice, minPurchaseAmount, maxPurchaseAmount, canBuyCredits, paused, className }) => {
  // Buying happens here rather than on the subscription page: the purchase refetches
  // the balance, the banner disappears and the upload card returns without a detour.
  const { open: buyOpen, onOpen: openBuy, onClose: closeBuy } = useOpen();
  const { open: contactOpen, onOpen: openContact, onClose: closeContact } = useOpen();
  const reserved = balance >= 1;

  return (
    <>
      <div className={classNames('flex flex-wrap items-center justify-between gap-3 rounded-md border border-warning-yellow-500 px-4 py-3 text-sm', className)}>
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-warning-yellow-700 dark:text-warning-yellow-500" />
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">{paused ? <T _str="Uploads are paused" swc /> : reserved ? <T _str="All remaining projects are reserved by uploads in progress" swc /> : <T _str="No projects left" swc />}</span>
            <span className="text-gray-600 dark:text-gray-300">
              {paused ? (
                <T _str="Your subscription is paused. Uploads resume when it does. Contact sales with any questions." />
              ) : reserved ? (
                <T _str="New uploads are possible once those uploads finish, or once projects are added." />
              ) : canBuyCredits ? (
                <T _str="Uploads are disabled until projects are added. Buy projects to continue. Or contact sales for a subscription with monthly projects included." />
              ) : (
                <T _str="Uploads are disabled until an admin of your organisation adds projects. Contact your organisation admin. Don't know who that is? Reach out to us." />
              )}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canBuyCredits && !paused && (
            <Button variant="primary" size="sm" onClick={openBuy}>
              <T _str="buy projects" swc />
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={openContact}>
            {canBuyCredits && !paused ? <T _str="contact sales" swc /> : <T _str="contact us" swc />}
          </Button>
        </div>
      </div>
      {canBuyCredits && <BuyCreditsModal open={buyOpen} onClose={closeBuy} organisationId={organisationId} projectUnitPrice={projectUnitPrice} minAmount={minPurchaseAmount} maxAmount={maxPurchaseAmount} />}
      <ContactSalesModal open={contactOpen} onClose={closeContact} organisationId={organisationId} />
    </>
  );
};

export const NoCreditsBanner = memo(NoCreditsBanner_);
